import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";

/** API klíče — `/admin/api/v1/api-keys` (JSON REST, API chain, JWT cookie). */
export interface ApiClient {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  createdAt: string | null;
}
export interface ApiKey {
  id: string;
  keyId: string;
  label: string | null;
  scopes: string[];
  roles: string[];
  enabled: boolean;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
}
export interface CreatedKey {
  id: string;
  keyId: string;
  plaintextValue: string;
  expiresAt: string | null;
}
export interface Catalog {
  scopes: { id: string; module: string; description: string }[];
  roles: { code: string; label: string; description: string }[];
}

export function useApiClients() {
  return useQuery({ queryKey: ["api-clients"], queryFn: () => apiFetch<ApiClient[]>("/admin/api/v1/api-keys/clients") });
}
export function useApiCatalog() {
  return useQuery({ queryKey: ["api-catalog"], queryFn: () => apiFetch<Catalog>("/admin/api/v1/api-keys/catalog"), staleTime: 60 * 60_000 });
}
export function useKeysForClient(clientId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["api-keys", clientId],
    queryFn: () => apiFetch<ApiKey[]>(`/admin/api/v1/api-keys/clients/${clientId}/keys`),
    enabled,
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string }) =>
      apiFetch<ApiClient>("/admin/api/v1/api-keys/clients", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-clients"] }),
  });
}
export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) => apiFetch<void>(`/admin/api/v1/api-keys/clients/${clientId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-clients"] }),
  });
}
export function useCreateKey(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { label?: string; scopes: string[]; roles: string[]; ttlDays?: number }) =>
      apiFetch<CreatedKey>(`/admin/api/v1/api-keys/clients/${clientId}/keys`, { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-keys", clientId] }),
  });
}
export function useRevokeKey(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyEntityId: string) =>
      apiFetch<void>(`/admin/api/v1/api-keys/keys/${keyEntityId}/revoke`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-keys", clientId] }),
  });
}
